import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { Popover } from 'primeng/popover';
import { EMPTY, Observable, of, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { AgentStatusService } from 'src/core/services/agent-status/agent-status.service';
import {
  getStatusLocalizationKey,
  isPredefinedStateName,
} from 'src/core/helpers/agent-status.helper';
import { CallFacadeService } from 'src/app/shared/agent-desktop/telephony/services/call-facade.service';
import { RingtoneService } from 'src/app/shared/agent-desktop/telephony/services/ringtone.service';
import { CallTabSyncService } from 'src/app/shared/agent-desktop/telephony/services/call-tab-sync.service';
import { RejectCallFlowService } from 'src/app/shared/agent-desktop/telephony/services/reject-call-flow.service';
import { parseSipPhoneNumber } from 'src/app/shared/agent-desktop/telephony/utils/parse-sip-phone-number';
import { findQueueTransferAgentName } from 'src/app/shared/agent-desktop/telephony/utils/find-queue-transfer-agent-name';
import { CallerContactResolverService } from 'src/app/shared/agent-desktop/services/caller-contact-resolver.service';
import { IncomingCallInfo } from 'src/app/shared/agent-desktop/telephony/models/incoming-call-info.model';
import { ConversationService } from 'src/app/features/conversation/services/conversation.service';
import { ConversationDto } from 'src/app/features/conversation/models/conversation.dto';
import { LocalizationDisplayService } from 'src/core/services/settings/localization-display.service';
import { AgentStatusDropdownComponent } from '../agent-status-dropdown/agent-status-dropdown.component';
import { IncomingCallToastComponent } from 'src/app/shared/agent-desktop/components/incoming-call-toast/incoming-call-toast.component';
import { RejectFlowOwner } from 'src/app/shared/agent-desktop/telephony/models/reject-flow-owner.type';
import { ScheduledCallOfferService } from 'src/app/shared/agent-desktop/scheduled-call/services/scheduled-call-offer.service';
import { ScheduledCallOfferToastComponent } from 'src/app/shared/agent-desktop/scheduled-call/components/scheduled-call-offer-toast/scheduled-call-offer-toast.component';
import { bindScheduledCallOfferToast } from 'src/app/shared/agent-desktop/scheduled-call/helpers/bind-scheduled-call-offer-toast';

const AGENT_DESKTOP_PATH = '/agent-desktop';
// Must match the hardcoded `key="incoming-call"` in incoming-call-toast.component.html. This
// component provides its own isolated MessageService instance (see providers below), so reusing the
// same key does not collide with AgentDesktopComponent's separate toast instance.
const INCOMING_TOAST_KEY = 'incoming-call';
const REJECT_FLOW_OWNER: RejectFlowOwner = 'status-toolbar';
// Same short poll AgentDesktopComponent uses to read a queue transfer's origin back from the
// conversation (a leg without the ConversationUid header only reaches its segment on answer).
const TRANSFER_ORIGIN_POLL_MS = 300;
const TRANSFER_ORIGIN_POLL_ATTEMPTS = 17;

/**
 * Agent status toolbar — registered once as a LeptonX NavItem, so it is present on every
 * authenticated page. Besides the status control (headset button + status dropdown), it also owns
 * the **route-independent incoming-call alert**: it renders the shared, presentational
 * `IncomingCallToastComponent` directly and drives it from `CallFacadeService`. An agent can see and
 * answer a ringing inbound call from any page, not only while the AgentDesktop route is active.
 *
 * The incoming-call concern is deliberately thin: no CRM panel — it mirrors the alerting + answer
 * half of AgentDesktopComponent's onIncomingCall$ handling, the caller's name (resolved by the shared
 * root-provided `CallerContactResolverService`) and the transfer origin (transferring agent and
 * queue), so a transferred call looks the same on every page, plus the decline-reason reject flow,
 * which is shared through the root-provided `RejectCallFlowService` (no duplicated state machine).
 * Rejecting therefore **stays on the current page**: the confirm + reason dialogs open right here and
 * the call is rejected without navigating anywhere. Accepting still hands the call off to
 * AgentDesktopComponent by navigating to `/agent-desktop`;
 * CallFacadeService/CallStateStore already carry the live session across that navigation
 * (root-provided singletons), so no state is lost or duplicated. It is suppressed whenever the agent
 * is already on the AgentDesktop route so it never duplicates that page's own incoming-call
 * toast/ringtone. See diagrams/03-incoming-call-flow.md.
 *
 * The incoming-call toast is rendered OUTSIDE the availability `[style.display]` gate (see the
 * template): a display:none ancestor removes descendants (including a position:fixed p-toast) from
 * the render tree, which would silently suppress the alert.
 *
 * The reject **dialog** deliberately does NOT live here — LeptonX's `.lpx-toolbar-container`
 * (`position: absolute; z-index: 1021`) is a stacking context, so a modal rendered inside it would
 * be painted below the side menu and every body-appended overlay. It is rendered by
 * `RejectCallDialogHostComponent` from `AppComponent` instead; this component only starts the flow.
 */
@Component({
  selector: 'app-agent-status-toolbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // NOTE: the `[style.display]` availability gate is NOT on the host — it lives on the status-button
  // wrapper in the template so the incoming-call toast can stay outside it (see class doc above).
  providers: [MessageService],
  imports: [
    CommonModule,
    LocalizationModule,
    TooltipModule,
    Popover,
    AgentStatusDropdownComponent,
    IncomingCallToastComponent,
    ScheduledCallOfferToastComponent,
  ],
  templateUrl: './agent-status-toolbar.component.html',
  styleUrls: ['./agent-status-toolbar.component.scss'],
})
export class AgentStatusToolbarComponent implements OnInit {
  readonly agentStatus = inject(AgentStatusService);
  private readonly localization = inject(LocalizationService);
  private readonly facade = inject(CallFacadeService);
  private readonly ringtone = inject(RingtoneService);
  private readonly callTabSync = inject(CallTabSyncService);
  private readonly rejectFlow = inject(RejectCallFlowService);
  private readonly callerContactResolver = inject(CallerContactResolverService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly scheduledCallOffer = inject(ScheduledCallOfferService);
  private readonly conversationService = inject(ConversationService);
  private readonly localizationDisplayService = inject(LocalizationDisplayService);

  @ViewChild('statusPanel') statusPanel!: Popover;

  readonly statusTooltip = computed(() => {
    const name = this.agentStatus.currentStateName();
    return isPredefinedStateName(name)
      ? this.localization.instant(getStatusLocalizationKey(name))
      : name;
  });

  // ─── Route-independent incoming-call alert ────────────────────────────────────────────────────
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  private readonly onAgentDesktopRoute = computed(() =>
    this.currentUrl().startsWith(AGENT_DESKTOP_PATH),
  );

  readonly ringtoneMuted = this.ringtone.muted;
  readonly answering = signal(false);
  readonly callerDetail = signal<string | null>(null);
  /** Full name of the registered CRM contact calling; null when unknown or the contact has no name. */
  readonly callerName = signal<string | null>(null);
  readonly queueName = signal<string | null>(null);
  readonly transferFromAgentName = signal<string | null>(null);
  readonly transferFromQueueName = signal<string | null>(null);

  /** Bumped per incoming call and on clear, so a late lookup never labels a later call's toast. */
  private incomingCallToken = 0;

  private readonly isInboundRinging = this.facade.isInboundRinging;

  readonly shouldAlert = computed(() => this.isInboundRinging() && !this.onAgentDesktopRoute());

  // Off the Agent Desktop route only; there AgentDesktopComponent owns the single offer toast (154880/7.5, K-172).
  readonly scheduledCallOfferData = this.scheduledCallOffer.offer;
  readonly scheduledCallOfferSeconds = this.scheduledCallOffer.remainingSeconds;
  readonly showScheduledCallOfferToast = bindScheduledCallOfferToast(
    () => this.scheduledCallOffer.toastVisible() && !this.onAgentDesktopRoute(),
  );

  constructor() {
    this.facade.onIncomingCall$
      .pipe(
        filter(() => !this.onAgentDesktopRoute()),
        tap(info => this.alertIncomingCall(info)),
        switchMap(info => (info.isInternal ? EMPTY : this.resolveCallerName())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(name => {
        if (this.shouldAlert()) {
          this.callerName.set(name);
        }
      });

    effect(
      () => {
        if (!this.shouldAlert()) {
          this.clearToast();
          this.ringtone.stop();
          this.answering.set(false);
          // Stale caller data would otherwise survive into the next off-route alert and, worse,
          // scope the next reject's decline-reason list to the previous call's queue.
          this.incomingCallToken++;
          this.queueName.set(null);
          this.callerDetail.set(null);
          this.callerName.set(null);
          this.transferFromAgentName.set(null);
          this.transferFromQueueName.set(null);
          // The call ended (caller hung up, another tab answered) or the agent moved onto the
          // AgentDesktop route: an open reject dialog belongs to a call this surface no longer
          // alerts on, so close it. Ownership is read in untracked(): RingtoneService is a root
          // singleton shared with AgentDesktopComponent, so if `owner` became a dependency here,
          // AgentDesktopComponent opening its own reject dialog would re-run this body on the
          // /agent-desktop route (where shouldAlert is permanently false) and silence its ringtone.
          untracked(() => {
            if (this.rejectFlow.isOwnedBy(REJECT_FLOW_OWNER)) {
              this.rejectFlow.reset();
            }
          });
        }
      },
      { injector: this.injector },
    );
  }

  ngOnInit(): void {
    void this.agentStatus.initSession();
  }

  async onStatusSelected(stateId: number): Promise<void> {
    await this.agentStatus.requestStatusChange(stateId);
  }

  onQueuedCleared(): void {
    this.agentStatus.setQueuedNextState(null);
  }

  onAnswer(): void {
    if (this.answering()) {
      return;
    }

    this.answering.set(true);
    this.clearToast();
    this.ringtone.stop();
    this.callTabSync.notifyAnswered();
    void this.answerAfterNavigation();
  }

  private async answerAfterNavigation(): Promise<void> {
    try {
      await this.router.navigateByUrl(AGENT_DESKTOP_PATH);
    } finally {
      await this.facade.answerCall();
    }
  }

  /**
   * Opens the decline-reason flow **in place** — no navigation. The agent stays on whatever page
   * they were on. The dialog itself is rendered by `RejectCallDialogHostComponent` at body level
   * (see that component for why it cannot live inside this toolbar); this method only starts the
   * shared flow.
   */
  onReject(): void {
    if (!this.isInboundRinging()) {
      return;
    }

    this.answering.set(false);
    this.rejectFlow.start(REJECT_FLOW_OWNER, {
      queueName: this.queueName(),
      // The backdrop covers this component's toast (it is trapped in the navbar's stacking context),
      // so the caller has to be repeated inside the dialog.
      callerLabel: this.callerName() ?? this.callerDetail(),
      // Host-owned cleanup: this component's own isolated toast instance and the ringtone.
      onRejectCommitted: () => {
        this.clearToast();
        this.ringtone.stop();
      },
    });
  }

  onMuteToggle(): void {
    if (!this.ringtoneMuted()) {
      this.ringtone.toggleMute(true);
    }
    this.clearToast();
    this.ringtone.stop();
  }

  /** Take (154880/8.1–8.2): silences the offer in every tab and opens Agent Desktop; the call waits for Call Now. */
  onScheduledCallOfferTake(): void {
    this.scheduledCallOffer.take();
    if (!this.onAgentDesktopRoute()) {
      void this.router.navigateByUrl(AGENT_DESKTOP_PATH);
    }
  }

  /** Abandon (154880/8.3): back to the pool through the API only; the agent stays on this page. */
  onScheduledCallOfferAbandon(): void {
    this.scheduledCallOffer.abandon();
  }

  private alertIncomingCall(info: IncomingCallInfo): void {
    const token = ++this.incomingCallToken;
    const phone = parseSipPhoneNumber(info.remoteUri);
    this.queueName.set(info.queueName);
    this.transferFromAgentName.set(info.transferFromAgentName);
    this.transferFromQueueName.set(info.transferFromQueueName);
    this.callerDetail.set(
      (phone ? this.localizationDisplayService.formatPhoneNumber(phone) : null) ||
        info.remoteDisplayName ||
        info.remoteUri,
    );
    this.callerName.set(null);
    this.ringtone.resetMute();
    this.showToast();
    this.ringtone.start();
    if (!info.isInternal) {
      this.lookupQueueTransferOrigin(info, token);
    }
  }

  private resolveCallerName(): Observable<string | null> {
    return this.callerContactResolver.resolveCurrentCaller().pipe(
      map(contact => `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim() || null),
      catchError(() => of(null)),
    );
  }

  private showToast(): void {
    this.messageService.clear(INCOMING_TOAST_KEY);
    this.messageService.add({
      key: INCOMING_TOAST_KEY,
      sticky: true,
      severity: 'info',
      closable: false,
      summary: this.localization.instant('AgentDesktop::Telephony:IncomingCall'),
      detail: this.callerDetail() ?? '',
    });
  }

  /**
   * A call transferred into a queue carries no `X-CX-Transfer-From` header (see
   * `findQueueTransferAgentName`), so its transferring agent is read back from the conversation —
   * mirrors AgentDesktopComponent.lookupQueueTransferOrigin. A transfer aimed at an agent already
   * arrives labelled.
   */
  private lookupQueueTransferOrigin(info: IncomingCallInfo, token: number): void {
    const sipCallId = info.sipCallId;
    if (info.transferFromAgentName || !sipCallId) {
      return;
    }

    timer(0, TRANSFER_ORIGIN_POLL_MS)
      .pipe(
        takeWhile(
          () =>
            token === this.incomingCallToken &&
            this.facade.currentSession()?.sipCallId === sipCallId,
        ),
        take(TRANSFER_ORIGIN_POLL_ATTEMPTS),
        exhaustMap(() => this.lookupConversation(sipCallId)),
        filter((conv): conv is ConversationDto => conv !== null),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(conv => {
        if (token !== this.incomingCallToken) {
          return;
        }
        const transferredBy = findQueueTransferAgentName(conv);
        if (transferredBy) {
          this.transferFromAgentName.set(transferredBy);
        }
      });
  }

  private lookupConversation(sipCallId: string): Observable<ConversationDto | null> {
    const conversationUid = this.facade.currentSession()?.conversationUid;
    return conversationUid
      ? this.conversationService.getByConversationUid(conversationUid)
      : this.conversationService.getByAgentLegCallId(sipCallId);
  }

  private clearToast(): void {
    this.messageService.clear(INCOMING_TOAST_KEY);
  }
}
